import Cookies from 'js-cookie';
import { GRAPHQL_HTTP_URL } from '../config/api';

const CACHE_KEY = 'tenantBranding:v1';
const CACHE_TTL_MS = 5 * 60 * 1000;
const EVENT_NAME = 'tenant-branding-loaded';

// Shared selection set for both the public (by host) and authenticated
// (by tenant) branding queries.
const BRANDING_FIELDS = `
  tenantId
  tenantName
  brandName
  logoUrl
  faviconUrl
  primaryColor
  secondaryColor
  accentColor
  hidePoweredBy
  loginHeadline
  loginSubtext
  loginBgImage
  loginBgColor
  domain
`;

const QUERY = `
  query GetTenantBranding($host: String!) {
    getTenantBranding(host: $host) { ${BRANDING_FIELDS} }
  }
`;

const MY_BRANDING_QUERY = `
  query GetMyBranding {
    getMyBranding { ${BRANDING_FIELDS} }
  }
`;

/** Reads the admin portal's JWT (set by the login flow) if the user is signed in. */
function getAuthToken() {
  try {
    return Cookies.get('accessToken') || null;
  } catch {
    return null;
  }
}

let current = null;

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.host || parsed.host !== window.location.host) return null;
    if (Date.now() - (parsed.ts ?? 0) > CACHE_TTL_MS) return null;
    return parsed.data ?? null;
  } catch {
    return null;
  }
}

function writeCache(data) {
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ host: window.location.host, ts: Date.now(), data }),
    );
  } catch {
    /* quota / private mode — ignore */
  }
}

function clearCache() {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    /* ignore */
  }
}

function setFavicon(url) {
  if (!url) return;
  try {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = url;
  } catch {
    /* ignore */
  }
}

// Map the tenant's brand colors onto the CSS variables the portal's components
// actually consume (--portal-primary / --portal-button-*), so white-label colors
// propagate through the whole UI — not just the login page. We only override a
// variable when the tenant has set the corresponding color, leaving the user's
// own dashboard theme preference intact otherwise.
function applyBrandColors(branding) {
  const root = document.documentElement.style;
  const { primaryColor, secondaryColor } = branding;
  if (primaryColor) {
    const strong = `color-mix(in srgb, ${primaryColor} 82%, #000)`;
    root.setProperty('--brand-primary', primaryColor);
    root.setProperty('--brand-primary-strong', strong);
    root.setProperty('--portal-primary', primaryColor);
    root.setProperty('--portal-primary-strong', strong);
    root.setProperty('--portal-primary-soft', `color-mix(in srgb, ${primaryColor} 12%, #fff)`);
    root.setProperty('--portal-button-primary', primaryColor);
  }
  if (secondaryColor) {
    root.setProperty('--brand-secondary', secondaryColor);
    root.setProperty('--portal-button-secondary', secondaryColor);
  }
  if (branding.accentColor) root.setProperty('--brand-accent', branding.accentColor);
}

function applyBranding(branding) {
  if (!branding) return;
  current = branding;
  applyBrandColors(branding);
  setFavicon(branding.faviconUrl);
  const title = branding.brandName || branding.tenantName;
  if (title) document.title = title;
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: branding }));
}

async function fetchBranding() {
  try {
    const res = await fetch(GRAPHQL_HTTP_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: QUERY,
        variables: { host: window.location.host },
      }),
    });
    if (!res.ok) return null;
    const body = await res.json();
    return body?.data?.getTenantBranding ?? null;
  } catch {
    return null;
  }
}

/** Authenticated branding for the signed-in user's own tenant (any host). */
async function fetchMyBranding(token) {
  try {
    const res = await fetch(GRAPHQL_HTTP_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ query: MY_BRANDING_QUERY }),
    });
    if (!res.ok) return null;
    const body = await res.json();
    return body?.data?.getMyBranding ?? null;
  } catch {
    return null;
  }
}

/**
 * Resolves the branding to apply, preferring the authenticated tenant-scoped
 * result (so white-label works on the shared SaaS domain too) and falling back
 * to the public host-based skin used on the pre-login screen.
 *
 * Returns `{ data, cacheable }` — only host-based branding is cached, since a
 * host maps to exactly one tenant; tenant branding is re-fetched each load to
 * avoid leaking one tenant's branding to another on a shared host.
 */
async function resolveBranding() {
  const token = getAuthToken();
  if (token) {
    const mine = await fetchMyBranding(token);
    if (mine) return { data: mine, cacheable: false };
  }
  const host = await fetchBranding();
  return host ? { data: host, cacheable: true } : null;
}

/**
 * Call early in app bootstrap. Fire-and-forget: applies cached branding
 * synchronously (if present) and refreshes from the backend in the background.
 */
export function bootstrapTenantBranding() {
  const cached = readCache();
  if (cached) applyBranding(cached);

  void resolveBranding().then((res) => {
    if (res?.data) {
      applyBranding(res.data);
      if (res.cacheable) writeCache(res.data);
      else clearCache();
    } else if (cached) {
      // Host is no longer an ACTIVE custom domain — drop the stale cache.
      clearCache();
      current = null;
    }
  });
}

/**
 * Re-fetch and apply branding after the user signs in, so a white-labeled
 * tenant's colors/logo take effect immediately without a full page reload.
 */
export async function refreshTenantBrandingFromAuth() {
  const token = getAuthToken();
  if (!token) return;
  const mine = await fetchMyBranding(token);
  if (mine) applyBranding(mine);
}

/**
 * Apply branding straight from a White-Label config record (the shape saved by
 * Settings → White Label). Used to update the live UI the instant the user
 * saves — no network round-trip and no backend dependency.
 */
export function applyBrandingFromConfig(config) {
  if (!config) return;
  const brand = config.brand || {};
  const login = config.login || {};
  applyBranding({
    tenantName: brand.brandName,
    brandName: brand.brandName,
    primaryColor: brand.primaryColor,
    secondaryColor: brand.secondaryColor,
    accentColor: brand.accentColor,
    faviconUrl: brand.favicon,
    logoUrl: brand.primaryLogo,
    loginHeadline: login.headline,
    loginSubtext: login.subtext,
    loginBgImage: login.bgImage,
    loginBgColor: login.bgColor,
    hidePoweredBy: Boolean(brand.hidePoweredBy) || Boolean(brand.removeSaasLogo),
  });
}

/** Synchronous accessor — returns null until first fetch resolves. */
export function getTenantBranding() {
  return current;
}

export const TENANT_BRANDING_EVENT = EVENT_NAME;
